import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  Star, Award, Zap, BarChart3, Activity, ChevronDown, Eye
} from 'lucide-react';

interface ProductData {
  name: string;
  category: string;
  revenue: number;
  units: number;
  growth: number;
  rating: number;
  color?: string;
}

interface ProductPerformanceMatrixProps {
  title: string;
  products: ProductData[];
}

const ProductPerformanceMatrix: React.FC<ProductPerformanceMatrixProps> = ({ 
  title, 
  products 
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'chart' | 'table'>('grid');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'revenue' | 'units' | 'growth'>('revenue');

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === 'revenue') return b.revenue - a.revenue;
    if (sortBy === 'units') return b.units - a.units;
    return b.growth - a.growth;
  });

  const maxRevenue = Math.max(...products.map(p => p.revenue));
  const maxUnits = Math.max(...products.map(p => p.units));

  const getProductColor = (index: number) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600',
      'from-purple-500 to-purple-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-cyan-500 to-cyan-600',
      'from-indigo-500 to-indigo-600',
      'from-red-500 to-red-600',
    ];
    return colors[index % colors.length];
  };

  const totalRevenue = products.reduce((sum, p) => sum + p.revenue, 0);
  const totalUnits = products.reduce((sum, p) => sum + p.units, 0);
  const avgGrowth = products.reduce((sum, p) => sum + p.growth, 0) / products.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Package className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'chart'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'table'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Activity className="w-4 h-4" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="revenue">Sort by Revenue</option>
              <option value="units">Sort by Units</option>
              <option value="growth">Sort by Growth</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200"
          >
            <Package className="w-8 h-8 text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{products.length}</div>
            <div className="text-sm text-gray-600">Total Products</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200"
          >
            <DollarSign className="w-8 h-8 text-green-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              ${(totalRevenue / 1000000).toFixed(2)}M
            </div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200"
          >
            <ShoppingCart className="w-8 h-8 text-purple-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {totalUnits.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Units Sold</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200"
          >
            <TrendingUp className="w-8 h-8 text-amber-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {avgGrowth > 0 ? '+' : ''}{avgGrowth.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Avg Growth</div>
          </motion.div>
        </div>
      </div>

      {/* Content Views */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' && (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {sortedProducts.map((product, index) => {
              const isSelected = selectedProduct === product.name;
              const revenuePercentage = (product.revenue / maxRevenue) * 100;

              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedProduct(isSelected ? null : product.name)}
                  className="cursor-pointer"
                >
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    className={`
                      bg-white rounded-2xl p-6 shadow-lg border-2 transition-all duration-300
                      ${isSelected ? 'border-blue-500 ring-4 ring-blue-100' : 'border-gray-200 hover:border-blue-300'}
                    `}
                  >
                    {/* Product Icon */}
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getProductColor(index)}
                      flex items-center justify-center shadow-lg mb-4 mx-auto`}>
                      <Package className="w-8 h-8 text-white" />
                    </div>

                    {/* Product Name */}
                    <h4 className="text-lg font-bold text-gray-900 text-center mb-2 truncate">
                      {product.name}
                    </h4>
                    <p className="text-xs text-gray-500 text-center mb-4">{product.category}</p>

                    {/* Revenue Circle */}
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="transform -rotate-90 w-32 h-32">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="#E5E7EB"
                          strokeWidth="8"
                          fill="none"
                        />
                        <motion.circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke={`url(#gradient-product-${index})`}
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 56}`}
                          initial={{ strokeDashoffset: 2 * Math.PI * 56 }}
                          animate={{
                            strokeDashoffset: 2 * Math.PI * 56 * (1 - revenuePercentage / 100)
                          }}
                          transition={{ duration: 1.5, delay: index * 0.1 }}
                        />
                        <defs>
                          <linearGradient id={`gradient-product-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={getProductColor(index).includes('blue') ? '#3B82F6' :
                              getProductColor(index).includes('green') ? '#10B981' :
                              getProductColor(index).includes('purple') ? '#8B5CF6' :
                              getProductColor(index).includes('orange') ? '#F97316' : '#EC4899'} />
                            <stop offset="100%" stopColor={getProductColor(index).includes('blue') ? '#2563EB' :
                              getProductColor(index).includes('green') ? '#059669' :
                              getProductColor(index).includes('purple') ? '#7C3AED' :
                              getProductColor(index).includes('orange') ? '#EA580C' : '#DB2777'} />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-xl font-black text-gray-900">
                            ${(product.revenue / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs text-gray-500">Revenue</div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <ShoppingCart className="w-4 h-4 mx-auto text-gray-600 mb-1" />
                        <div className="text-sm font-bold text-gray-900">{product.units}</div>
                        <div className="text-xs text-gray-500">Units</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <TrendingUp className={`w-4 h-4 mx-auto mb-1 ${product.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                        <div className={`text-sm font-bold ${product.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.growth > 0 ? '+' : ''}{product.growth}%
                        </div>
                        <div className="text-xs text-gray-500">Growth</div>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center justify-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < product.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-4 pt-4 border-t border-gray-200 overflow-hidden"
                        >
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Revenue Share:</span>
                              <span className="font-semibold text-gray-900">
                                {((product.revenue / totalRevenue) * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Avg Price:</span>
                              <span className="font-semibold text-gray-900">
                                ${(product.revenue / product.units).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {viewMode === 'chart' && (
          <motion.div
            key="chart"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          >
            <h4 className="text-xl font-bold text-gray-900 mb-6">Revenue Comparison</h4>
            <div className="space-y-4">
              {sortedProducts.map((product, index) => {
                const percentage = (product.revenue / maxRevenue) * 100;

                return (
                  <div key={product.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getProductColor(index)}
                          flex items-center justify-center shadow-md`}>
                          <Package className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.category}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          ${(product.revenue / 1000).toFixed(0)}K
                        </div>
                        <div className="text-xs text-gray-500">{product.units} units</div>
                      </div>
                    </div>

                    <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getProductColor(index)} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {viewMode === 'table' && (
          <motion.div
            key="table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Units
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Growth
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedProducts.map((product, index) => (
                    <motion.tr
                      key={product.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getProductColor(index)}
                            flex items-center justify-center shadow-md`}>
                            <Package className="w-5 h-5 text-white" />
                          </div>
                          <div className="font-semibold text-gray-900">{product.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {product.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-bold text-gray-900">
                          ${(product.revenue / 1000).toFixed(0)}K
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">{product.units.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                          ${product.growth >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {product.growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {product.growth > 0 ? '+' : ''}{product.growth}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < product.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductPerformanceMatrix;

